/**
 * /admin/scorer-marketplace — moderation queue for scorer profiles.
 *
 * Tabs by status (Pending / Listed / Denied / Withdrawn / All).
 * Click a row → side drawer with the full profile + action buttons:
 *   - Approve (PENDING → LISTED)
 *   - Deny    (PENDING/LISTED → DENIED, with internal note + customer-facing reason)
 *   - Withdraw (LISTED → WITHDRAWN, admin override with reason)
 *   - Issue / Revoke verified badge (independent of status)
 *
 * Backend wires through services_scorer_marketplace which writes
 * ZenAdminAudit + fires the listing-{approved,denied} notifications.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  RefreshCw, X, Search, ShieldCheck, ShieldOff, CheckCircle2, XCircle,
  AlertTriangle, Loader2, BadgeCheck, EyeOff, Calendar, MapPin, Languages,
} from "lucide-react";
import {
  listScorerProfiles,
  getScorerProfile,
  approveScorerProfile,
  denyScorerProfile,
  withdrawScorerProfile,
  issueScorerBadge,
  revokeScorerBadge,
} from "../../api/owner.api";
import { toArray } from "../../utils/toArray";

const TABS = [
  { key: "pending",   label: "Pending",   hint: "Awaiting review — work here first." },
  { key: "listed",    label: "Listed",    hint: "Currently public." },
  { key: "denied",    label: "Denied",    hint: "Rejected; user can re-apply." },
  { key: "withdrawn", label: "Withdrawn", hint: "User or system pulled the listing." },
  { key: "all",       label: "All",       hint: "Every profile, all statuses." },
];

const STATUS_STYLE = {
  DRAFT:     "text-text-secondary",
  PENDING:   "text-amber-300",
  LISTED:    "text-emerald-300",
  DENIED:    "text-rose-300",
  WITHDRAWN: "text-text-secondary",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminScorerMarketplace() {
  // Tab + search live in the URL so refresh / share / browser-back all
  // preserve filter context. Dashboard's "Scorer listings pending
  // review" card lands here with no querystring; the default "pending"
  // tab covers that case automatically.
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_KEYS = ["pending", "listed", "denied", "withdrawn", "all"];
  const rawTab = searchParams.get("tab") || "pending";
  const tab = TAB_KEYS.includes(rawTab) ? rawTab : "pending";
  const search = searchParams.get("q") || "";

  function setTab(next) {
    const sp = new URLSearchParams(searchParams);
    if (next === "pending") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  }
  function setSearch(next) {
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set("q", next);
    else sp.delete("q");
    setSearchParams(sp, { replace: true });
  }

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  async function fetchPage(p) {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listScorerProfiles({
        status: tab,
        search: search || undefined,
        page: p,
      });
      setRows(toArray(data));
      setCount(data?.count || 0);
      if (p === 1 && page !== 1) setPage(1);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search]);
  useEffect(() => { fetchPage(page); /* eslint-disable-next-line */ }, [page]);

  const totalPages = Math.max(1, Math.ceil(count / 25));

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Scorer marketplace</h1>
          <p className="text-sm text-text-secondary mt-1">
            B2C_ELITE perk — find-a-scorer public listings. Approve / deny
            new applications, issue verified badges, force-withdraw on
            complaints.
          </p>
        </div>
        <button
          onClick={() => fetchPage(page)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              title={t.hint}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active
                  ? "bg-button-primary text-white border-button-primary"
                  : "bg-bg-primary border-border-default text-text-secondary hover:text-text-primary hover:border-text-secondary"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Display name or email…"
            className="w-full pl-9 pr-3 py-2 rounded-md bg-input border border-border-default text-sm focus:outline-none focus:border-button-primary"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-lg border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-table-header">
            <tr className="text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <th className="px-3 py-2">Display name</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 hidden md:table-cell">Badge</th>
              <th className="px-3 py-2 hidden md:table-cell">Regions</th>
              <th className="px-3 py-2 hidden lg:table-cell">Formats</th>
              <th className="px-3 py-2 text-right hidden md:table-cell">Day rate</th>
              <th className="px-3 py-2 hidden md:table-cell">Applied</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-text-secondary">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-text-secondary">
                No profiles in this tab.
              </td></tr>
            )}
            {rows.map((p) => (
              <tr
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className="border-t border-border-default hover:bg-bg-secondary cursor-pointer"
              >
                <td className="px-3 py-2 text-sm">{p.display_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-mono">{p.user_email}</div>
                  <div className="text-text-secondary">{p.user_full_name}</div>
                </td>
                <td className={`px-3 py-2 text-xs font-semibold ${STATUS_STYLE[p.status] || ""}`}>
                  ● {p.status}
                </td>
                <td className="px-3 py-2 hidden md:table-cell">
                  {p.has_verified_badge
                    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                        <BadgeCheck className="w-3.5 h-3.5" /> Verified
                      </span>
                    : <span className="text-xs text-text-secondary">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-text-secondary hidden md:table-cell truncate max-w-[180px]">
                  {(p.regions || []).join(", ") || "—"}
                </td>
                <td className="px-3 py-2 text-xs text-text-secondary hidden lg:table-cell truncate max-w-[160px]">
                  {(p.formats_scored || []).join(", ") || "—"}
                </td>
                <td className="px-3 py-2 text-right text-xs tabular-nums hidden md:table-cell">
                  {p.daily_rate_inr
                    ? `₹${p.daily_rate_inr.toLocaleString("en-IN")}`
                    : "—"}
                </td>
                <td className="px-3 py-2 text-xs text-text-secondary hidden md:table-cell whitespace-nowrap">
                  {fmtDate(p.applied_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-text-secondary">
        <div>Page {page} of {totalPages} · {count} profiles</div>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Prev</button>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 rounded border border-border-default disabled:opacity-40 hover:bg-bg-secondary"
          >Next</button>
        </div>
      </div>

      {selectedId && (
        <ScorerDrawer
          profileId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => fetchPage(page)}
        />
      )}
    </div>
  );
}


function ScorerDrawer({ profileId, onClose, onChanged }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const panelRef = useRef(null);
  useDialogDismiss({ open: true, onClose, ref: panelRef });
  // Inline form state for the deny action.
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyInternal, setDenyInternal] = useState("");
  const [denyCustomer, setDenyCustomer] = useState("");
  // Withdraw reason.
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getScorerProfile(profileId)
      .then((r) => { if (alive) setProfile(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [profileId]);

  async function runAction(fn, args) {
    setBusy(true);
    setError(null);
    try {
      const r = await fn(...args);
      setProfile(r.data);
      onChanged?.();
      setDenyOpen(false);
      setWithdrawOpen(false);
      setDenyInternal("");
      setDenyCustomer("");
      setWithdrawReason("");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  const canApprove = profile && profile.status === "PENDING";
  const canDeny = profile && (profile.status === "PENDING" || profile.status === "LISTED");
  const canWithdraw = profile && profile.status === "LISTED";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="presentation">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={profile?.display_name ? `Scorer profile ${profile.display_name}` : "Scorer profile"}
        className="relative w-full max-w-lg bg-bg-primary border-l border-border-default h-full overflow-y-auto"
      >
        <header className="flex items-start justify-between p-4 border-b border-border-default sticky top-0 bg-bg-primary z-10">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-text-secondary">
              Scorer profile
            </div>
            <div className="font-semibold truncate">{profile?.display_name || "—"}</div>
            <div className="font-mono text-xs text-text-secondary truncate">{profile?.user_email}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-bg-secondary">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {loading && <div className="text-sm text-text-secondary">Loading profile…</div>}
          {error && (
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          {profile && (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Kv label="Status">
                  <span className={`font-semibold ${STATUS_STYLE[profile.status] || ""}`}>
                    {profile.status}
                  </span>
                </Kv>
                <Kv label="Badge">
                  {profile.has_verified_badge
                    ? <span className="text-emerald-300 inline-flex items-center gap-1">
                        <BadgeCheck className="w-3.5 h-3.5" /> Verified
                      </span>
                    : <span className="text-text-secondary">None</span>}
                </Kv>
                <Kv label="Day rate">
                  {profile.daily_rate_inr
                    ? `₹${profile.daily_rate_inr.toLocaleString("en-IN")}`
                    : "—"}
                </Kv>
                <Kv label="Years experience">
                  {profile.years_experience ?? "—"}
                </Kv>
              </div>

              <section className="space-y-1 text-sm">
                <div className="text-[10px] uppercase tracking-widest text-text-secondary">Bio</div>
                <div className="rounded border border-border-default p-2 text-text-primary text-xs whitespace-pre-wrap min-h-[40px]">
                  {profile.bio || <span className="text-text-secondary">—</span>}
                </div>
              </section>

              <div className="grid grid-cols-1 gap-2 text-xs">
                <Kv icon={<MapPin className="w-3 h-3" />} label="Regions">
                  {(profile.regions || []).join(", ") || "—"}
                </Kv>
                <Kv icon={<Languages className="w-3 h-3" />} label="Languages">
                  {(profile.languages || []).join(", ") || "—"}
                </Kv>
                <Kv label="Formats">
                  {(profile.formats_scored || []).join(", ") || "—"}
                </Kv>
                <Kv icon={<Calendar className="w-3 h-3" />} label="Applied">
                  {fmtDate(profile.applied_at)}
                </Kv>
                <Kv icon={<Calendar className="w-3 h-3" />} label="Listed">
                  {fmtDate(profile.listed_at)}
                </Kv>
                <Kv icon={<Calendar className="w-3 h-3" />} label="Denied">
                  {fmtDate(profile.denied_at)}
                </Kv>
              </div>

              {profile.admin_internal_note && (
                <section className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-text-secondary">Admin internal notes</div>
                  <pre className="rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[11px] whitespace-pre-wrap p-2">
                    {profile.admin_internal_note}
                  </pre>
                </section>
              )}

              {profile.sample_match_urls?.length > 0 && (
                <section className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-text-secondary">Sample matches</div>
                  <ul className="text-xs space-y-0.5">
                    {profile.sample_match_urls.map((u, i) => (
                      <li key={i}>
                        <a href={u} target="_blank" rel="noopener noreferrer" className="text-button-primary hover:underline break-all">
                          {u}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="rounded border border-border-default p-2 text-xs">
                <Link
                  to={`/admin/users?segment=all&search=${encodeURIComponent(profile.user_email || "")}`}
                  className="text-button-primary hover:underline"
                >
                  Open user record →
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        {profile && (
          <footer className="p-4 border-t border-border-default space-y-2 sticky bottom-0 bg-bg-primary">
            <div className="grid grid-cols-2 gap-2">
              <button
                disabled={busy || !canApprove}
                onClick={() => runAction(approveScorerProfile, [profile.id])}
                className="px-3 py-2 rounded border border-emerald-500/40 text-emerald-300 text-sm hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" /> Approve
              </button>
              <button
                disabled={busy || !canDeny}
                onClick={() => setDenyOpen((v) => !v)}
                className="px-3 py-2 rounded border border-rose-500/40 text-rose-300 text-sm hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" /> Deny…
              </button>
            </div>

            {denyOpen && canDeny && (
              <div className="rounded border border-rose-500/40 bg-rose-500/5 p-2 space-y-2 text-xs">
                <label className="block">
                  <span className="text-text-secondary">Internal note (admin-only, appended to profile)</span>
                  <textarea
                    rows={2}
                    value={denyInternal}
                    onChange={(e) => setDenyInternal(e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 rounded border border-border-default bg-bg-secondary text-text-primary"
                  />
                </label>
                <label className="block">
                  <span className="text-text-secondary">Customer-facing reason (emailed to scorer; keep polite + actionable)</span>
                  <textarea
                    rows={2}
                    value={denyCustomer}
                    onChange={(e) => setDenyCustomer(e.target.value)}
                    placeholder="e.g. Profile photo too dark — please re-upload."
                    className="mt-1 w-full px-2 py-1.5 rounded border border-border-default bg-bg-secondary text-text-primary"
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <button
                    disabled={busy}
                    onClick={() => setDenyOpen(false)}
                    className="px-2 py-1 rounded border border-border-default hover:bg-bg-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => runAction(denyScorerProfile, [profile.id, { internalNote: denyInternal, customerFacingReason: denyCustomer }])}
                    className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-500 text-white font-semibold flex items-center gap-1"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                    Confirm deny
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                disabled={busy || profile.has_verified_badge}
                onClick={() => runAction(issueScorerBadge, [profile.id])}
                className="px-3 py-2 rounded border border-blue-500/40 text-blue-300 text-sm hover:bg-blue-500/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ShieldCheck className="w-4 h-4" /> Issue badge
              </button>
              <button
                disabled={busy || !profile.has_verified_badge}
                onClick={() => {
                  const reason = window.prompt("Reason for revoking the verified badge?") || "";
                  if (!reason) return;
                  runAction(revokeScorerBadge, [profile.id, reason]);
                }}
                className="px-3 py-2 rounded border border-border-default text-text-secondary text-sm hover:bg-bg-secondary disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ShieldOff className="w-4 h-4" /> Revoke badge
              </button>
            </div>

            <button
              disabled={busy || !canWithdraw}
              onClick={() => setWithdrawOpen((v) => !v)}
              className="w-full px-3 py-2 rounded border border-amber-500/40 text-amber-300 text-sm hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <EyeOff className="w-4 h-4" /> Force withdraw…
            </button>

            {withdrawOpen && canWithdraw && (
              <div className="rounded border border-amber-500/40 bg-amber-500/5 p-2 space-y-2 text-xs">
                <label className="block">
                  <span className="text-text-secondary">Reason (logged in admin notes + audit ledger)</span>
                  <input
                    value={withdrawReason}
                    onChange={(e) => setWithdrawReason(e.target.value)}
                    placeholder="e.g. Customer complaint pending investigation"
                    className="mt-1 w-full px-2 py-1.5 rounded border border-border-default bg-bg-secondary text-text-primary"
                  />
                </label>
                <div className="flex justify-end gap-2">
                  <button
                    disabled={busy}
                    onClick={() => setWithdrawOpen(false)}
                    className="px-2 py-1 rounded border border-border-default hover:bg-bg-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={busy || !withdrawReason.trim()}
                    onClick={() => runAction(withdrawScorerProfile, [profile.id, withdrawReason.trim()])}
                    className="px-2 py-1 rounded bg-amber-600 hover:bg-amber-500 text-white font-semibold flex items-center gap-1"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />}
                    Withdraw
                  </button>
                </div>
              </div>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}


function Kv({ label, icon, children }) {
  return (
    <div className="rounded border border-border-default p-2">
      <div className="text-[10px] uppercase tracking-widest text-text-secondary flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-sm mt-0.5">{children}</div>
    </div>
  );
}
